import { type RefObject, useEffect, useRef } from "react"

interface Options {
	scrollRef: RefObject<HTMLElement | null>
	enabled: boolean
	threshold?: number
	/** How long the container must stay at-bottom before firing. Default 2s. */
	delayMs?: number
	onSettled: () => void
}

/**
 * Calls `onSettled` once when the scroll container has been at its visual
 * "bottom" continuously for `delayMs`. Re-arms after the user scrolls
 * away and back.
 *
 * Works for both flex-col-reverse layouts (visual bottom = scrollTop ≈ 0)
 * and normal layouts (visual bottom = scrollHeight - clientHeight - scrollTop ≈ 0)
 * by treating either side within `threshold` px as "at bottom".
 */
export function useBottomVisibleTrigger({
	scrollRef,
	enabled,
	threshold = 4,
	delayMs = 2000,
	onSettled,
}: Options): void {
	const callbackRef = useRef(onSettled)
	useEffect(() => {
		callbackRef.current = onSettled
	}, [onSettled])

	useEffect(() => {
		if (!enabled) return
		const el = scrollRef.current
		if (!el) return
		let timer: ReturnType<typeof setTimeout> | null = null
		let firedFor: number | null = null
		const isAtBottom = () => {
			const distFromTop = el.scrollTop
			const distFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop
			return distFromTop <= threshold || distFromBottom <= threshold
		}
		const check = () => {
			if (!isAtBottom()) {
				if (timer) clearTimeout(timer)
				timer = null
				firedFor = null
				return
			}
			if (timer) return
			timer = setTimeout(() => {
				timer = null
				if (!isAtBottom()) return
				if (firedFor === el.scrollHeight) return
				firedFor = el.scrollHeight
				callbackRef.current()
			}, delayMs)
		}
		check()
		el.addEventListener("scroll", check, { passive: true })
		const ro = new ResizeObserver(check)
		ro.observe(el)
		return () => {
			el.removeEventListener("scroll", check)
			ro.disconnect()
			if (timer) clearTimeout(timer)
		}
	}, [scrollRef, enabled, threshold, delayMs])
}
